import { IAnswer, IQuestion } from '@sdk/contracts';
import { Radio } from 'antd';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { PluggableList } from 'react-markdown/lib/react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';

import { AnswerTextWrapper, AnswerWrapper, FormulaWrapper, StyledImage } from './AnswerItem.styles';

import { Icon } from '@/components/Icon';
import { useResponseForQuestion } from '@/hooks';

const ICON_SIZE = 24;
const IMAGE_HEIGHT = 150;

type Props = {
  question: IQuestion;
  answer: IAnswer;
  index: number;
  showAnswer: boolean;
};

const AnswerItemComponent: React.FC<Props> = ({ question, answer, index, showAnswer }) => {
  const currentResponse = useResponseForQuestion(question._id);

  const isCorrect = useMemo(() => {
    return showAnswer && index === question.correctAnswer;
  }, [showAnswer, index, question.correctAnswer]);

  const isSelected = useMemo(() => {
    return currentResponse === index;
  }, [currentResponse, index]);

  const isIncorrect = useMemo(() => {
    return showAnswer && isSelected && !isCorrect;
  }, [showAnswer, isSelected, isCorrect]);

  return (
    <Radio
      value={index}
      className={isCorrect ? 'correct' : isIncorrect ? 'incorrect' : isSelected ? 'selected' : ''}>
      <AnswerWrapper>
        <AnswerTextWrapper>
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex] as PluggableList}>
            {answer.text}
          </ReactMarkdown>
          {answer.formula && (
            <FormulaWrapper dangerouslySetInnerHTML={{ __html: answer.formula }} />
          )}
          {answer.file && (
            <StyledImage
              onClick={e => e.stopPropagation()}
              src={answer.file.url}
              alt={answer.file.name}
              height={IMAGE_HEIGHT}
              loading="lazy"
            />
          )}
        </AnswerTextWrapper>
        <Icon
          style={{
            visibility:
              showAnswer && ((isCorrect && currentResponse === index) || isIncorrect)
                ? 'visible'
                : 'hidden'
          }}
          i={isCorrect ? 'CheckmarkFilled' : 'CloseFilled'}
          color={isCorrect ? 'green6' : 'red6'}
          size={ICON_SIZE}
        />
      </AnswerWrapper>
    </Radio>
  );
};

export const AnswerItem = React.memo(AnswerItemComponent);
